'use client';

import { useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  LocationAutocomplete,
  type CityAutocompleteResult,
} from '@/app/home/(user)/_components/location-autocomplete';
import { MapPin, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Trans } from '@kit/ui/trans';

import {
  formatNear,
  parseNear,
  parseRadius,
  RADIUS_OPTIONS_KM,
} from '../_lib/location-params';
import {
  saveLocationOff,
  saveStoredLocation,
} from '../_lib/location-preference';

/**
 * Location facet (FB-Marketplace model): a city picker + "within X km" radius,
 * URL-driven like every other facet (`near`/`radius`/`city` params, cursor
 * reset on change). The chosen location is persisted to localStorage so the
 * next visit re-applies it (marketplace-location-init); clearing writes an
 * explicit off-flag so auto-detect stays off.
 *
 * Cross-route-group import of `LocationAutocomplete` follows the existing
 * precedent (the sell wizard's step-price-delivery.tsx imports the same
 * component).
 */
export function LocationFilter() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const [changing, setChanging] = useState(false);

  const nearPoint = parseNear(searchParams.get('near') ?? undefined);
  const radiusKm = parseRadius(searchParams.get('radius') ?? undefined);
  const cityLabel = searchParams.get('city');
  const pickupOnly = searchParams.get('delivery') === 'pickup';

  const pushParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('cursor');
      mutate(params);
      const qs = params.toString();
      router.push(qs ? `/marketplace?${qs}` : '/marketplace');
    },
    [router, searchParams],
  );

  const applyLocation = useCallback(
    (location: CityAutocompleteResult | null) => {
      if (
        !location ||
        location.latitude === null ||
        location.longitude === null
      ) {
        return;
      }
      setChanging(false);
      saveStoredLocation({
        city: location.city,
        lat: location.latitude,
        lng: location.longitude,
        radius: radiusKm,
      });
      pushParams((params) => {
        params.set(
          'near',
          formatNear({ lat: location.latitude!, lng: location.longitude! }),
        );
        params.set('radius', String(radiusKm));
        params.set('city', location.city);
      });
    },
    [pushParams, radiusKm],
  );

  const applyRadius = useCallback(
    (value: string) => {
      if (!nearPoint) return;
      saveStoredLocation({
        city: cityLabel ?? '',
        lat: nearPoint.lat,
        lng: nearPoint.lng,
        radius: Number(value),
      });
      pushParams((params) => {
        params.set('radius', value);
      });
    },
    [pushParams, nearPoint, cityLabel],
  );

  const togglePickupOnly = useCallback(
    (checked: boolean) => {
      pushParams((params) => {
        if (checked) {
          params.set('delivery', 'pickup');
        } else {
          params.delete('delivery');
        }
      });
    },
    [pushParams],
  );

  const clearLocation = useCallback(() => {
    setChanging(false);
    saveLocationOff();
    pushParams((params) => {
      params.delete('near');
      params.delete('radius');
      params.delete('city');
    });
  }, [pushParams]);

  if (!nearPoint || changing) {
    return (
      <div className="space-y-2">
        <LocationAutocomplete
          value={null}
          onChange={applyLocation}
          label=""
          aria-label={t('explore:locationFilter.cityAriaLabel')}
          placeholder={t('explore:locationFilter.cityPlaceholder')}
        />
        {changing && (
          <Button
            type="button"
            variant="link"
            onClick={() => setChanging(false)}
            className="text-muted-foreground hover:text-foreground h-auto p-0 text-sm underline-offset-4 hover:underline"
          >
            {t('explore:locationFilter.keep', {
              location:
                cityLabel ?? t('explore:locationFilter.currentLocation'),
            })}
          </Button>
        )}
        {/* Delivery method (FB-style): pickup-only narrows to fetchable items
          and removes the shipping escape from the radius predicate. Works
          with or without an active location. */}
        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id="marketplace-pickup-only"
            checked={pickupOnly}
            onCheckedChange={(v) => togglePickupOnly(v === true)}
          />
          <Label
            htmlFor="marketplace-pickup-only"
            className="text-sm font-normal"
          >
            {t('explore:locationFilter.localPickupOnly')}
          </Label>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <MapPin className="text-muted-foreground size-4 shrink-0" aria-hidden />
        <span className="flex-1 truncate text-sm font-medium">
          {cityLabel ?? `${nearPoint.lat}, ${nearPoint.lng}`}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={clearLocation}
          aria-label={t('explore:locationFilter.clearAriaLabel')}
          className="text-muted-foreground hover:text-foreground h-auto w-auto rounded-md p-1"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      <Select value={String(radiusKm)} onValueChange={applyRadius}>
        <SelectTrigger aria-label={t('explore:locationFilter.radiusAriaLabel')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RADIUS_OPTIONS_KM.map((km) => (
            <SelectItem key={km} value={String(km)}>
              {t('explore:locationFilter.withinKm', { km })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant="link"
        onClick={() => setChanging(true)}
        className="text-muted-foreground hover:text-foreground h-auto p-0 text-sm underline-offset-4 hover:underline"
      >
        {t('explore:locationFilter.changeLocation')}
      </Button>
      {/* Delivery method (FB-style): pickup-only narrows to fetchable items
          and removes the shipping escape from the radius predicate. Works
          with or without an active location. */}
      <div className="flex items-center gap-2 pt-1">
        <Checkbox
          id="marketplace-pickup-only"
          checked={pickupOnly}
          onCheckedChange={(v) => togglePickupOnly(v === true)}
        />
        <Label
          htmlFor="marketplace-pickup-only"
          className="text-sm font-normal"
        >
          <Trans i18nKey="explore:locationFilter.localPickupOnly" />
        </Label>
      </div>
    </div>
  );
}
